import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import type { EChartsOption, SeriesOption } from 'echarts';
import { EMPTY, Observable, forkJoin, map, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';
import { transformEcharts } from '@features/nuvem/chart/echarts.util';
import type { ChartDataResponse } from '@features/nuvem/models/pergunta.models';
import type { RegbrChartParams, RegbrFilterOption } from './regbr.models';

export interface RegbrFilters {
  leis: RegbrFilterOption[];
  leisAgenciaReguladora: RegbrFilterOption[];
  agencias: RegbrFilterOption[];
  setores: RegbrFilterOption[];
  situacoes: RegbrFilterOption[];
}

const AGENCY_LAW_TYPES = new Set([
  'Portaria',
  'Resolução',
  'Súmula',
  'Instrução Normativa',
]);

@Injectable({ providedIn: 'root' })
export class RegbrService {
  private readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly url = `${environment.apiUrl}/regdata`;
  private readonly filtersCache = new Map<string, Observable<RegbrFilters>>();

  getFilters(): Observable<RegbrFilters> {
    if (!this.isBrowser) return EMPTY;
    const lang = this.transloco.getActiveLang();
    let filters$ = this.filtersCache.get(lang);

    if (!filters$) {
      filters$ = forkJoin({
        leis: this.getFiltro('tipo_lei'),
        agencias: this.getFiltro('agencia_reguladora'),
        setores: this.getFiltro('setor'),
        situacoes: this.getFiltro('situacao'),
      }).pipe(
        map(({ leis, agencias, setores, situacoes }) => {
          const sortedLeis = sortOptions(leis);
          const sortedSituacoes = sortOptions(situacoes);

          return {
            leis: sortedLeis,
            leisAgenciaReguladora: sortedLeis.filter((item) =>
              AGENCY_LAW_TYPES.has(item.value),
            ),
            agencias: sortOptions(agencias),
            setores: sortOptions(setores),
            situacoes: [
              {
                label: this.transloco.translate('pages.regbr.filters.active'),
                value: 'Em Vigência',
                items: sortedSituacoes.filter(
                  (s) => s.value === 'Não consta revogação expressa',
                ),
              },
              {
                label: this.transloco.translate('pages.regbr.filters.inactive'),
                value: 'Vigência encerrada',
                items: sortedSituacoes.filter(
                  (s) => s.value !== 'Não consta revogação expressa',
                ),
              },
            ],
          };
        }),
        shareReplay(1),
      );
      this.filtersCache.set(lang, filters$);
    }

    return filters$;
  }

  getChart(params: RegbrChartParams): Observable<EChartsOption> {
    if (!this.isBrowser) return EMPTY;

    return this.http.post<ChartDataResponse>(`${this.url}/grafico`, params).pipe(
      map((response) => transformEcharts(structuredClone(response), '', this.locale)),
      map((chart) => this.prepareRegbrChart(chart)),
    );
  }

  private getFiltro(filtro: string): Observable<RegbrFilterOption[]> {
    return this.http.get<RegbrFilterOption[]>(`${this.url}/filtro/${filtro}`);
  }

  private prepareRegbrChart(chart: EChartsOption): EChartsOption {
    const series = Array.isArray(chart.series) ? chart.series : [];
    chart.series = series
      .filter((item) => item.name !== 'Não regulatório')
      .map((item) => {
        const prepared = { ...item } as SeriesOption & Record<string, unknown>;
        prepared['type'] = 'line';
        prepared['stack'] = 'one';
        prepared['areaStyle'] = {};
        prepared['emphasis'] = { focus: 'series' };
        prepared['showSymbol'] = false;
        return prepared;
      });

    chart.grid = {
      ...(typeof chart.grid === 'object' && !Array.isArray(chart.grid) ? chart.grid : {}),
      bottom: 56,
    };

    return chart;
  }

  private get locale(): 'pt-BR' | 'en' {
    return this.transloco.getActiveLang() === 'en' ? 'en' : 'pt-BR';
  }
}

export function flattenRegbrOptions(options: RegbrFilterOption[]): RegbrFilterOption[] {
  return options.flatMap((option) => option.items ?? [option]);
}

export function optionValues(options: RegbrFilterOption[]): string[] {
  return flattenRegbrOptions(options).map((option) => option.value);
}

function sortOptions(options: RegbrFilterOption[]): RegbrFilterOption[] {
  return [...options]
    .map((option) => ({
      ...option,
      items: option.items ? sortOptions(option.items) : undefined,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}
